import { useId, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Globe, Lock } from "lucide-react";
import { cn } from "@nexus/ui";
import type { TemplateScopeMode } from "@/features/identity-hub/api/identity-hub.api";

interface TemplateScopeChoiceProps {
  value: TemplateScopeMode;
  onValueChange: (value: TemplateScopeMode) => void;
  /** Scope is fixed once a role exists. */
  locked: boolean;
}

interface ScopeOption {
  value: TemplateScopeMode;
  Icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

/** Fixed vs wildcard catalog-role scope as two radio tiles. */
export function TemplateScopeChoice({ value, onValueChange, locked }: TemplateScopeChoiceProps) {
  const { t } = useTranslation();
  const name = useId();
  const options: ScopeOption[] = [
    {
      value: "FIXED",
      Icon: Building2,
      title: t("iam.templates.dialog.scopeFixedTitle", "Specific organizations"),
      description: t("iam.templates.dialog.scopeFixedDescription", "Only the organizations you choose below."),
    },
    {
      value: "WILDCARD",
      Icon: Globe,
      title: t("iam.templates.dialog.scopeWildcardTitle", "Any organization"),
      description: t(
        "iam.templates.dialog.scopeWildcardDescription",
        "Every organization you can grant to, including ones added later.",
      ),
    },
  ];

  return (
    <div
      role="radiogroup"
      aria-label={t("iam.templates.scopeModeAria", "Role scope")}
      className="grid grid-cols-1 gap-3 sm:grid-cols-2"
    >
      {options.map(({ value: optionValue, Icon, title, description }) => {
        const isChecked = value === optionValue;
        return (
          <label
            key={optionValue}
            className={cn(
              "flex items-start gap-3 rounded-lg border p-3.5 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/40",
              isChecked ? "border-primary bg-primary-subtle" : "border-border bg-background",
              locked ? "cursor-not-allowed" : "cursor-pointer hover:border-foreground/30",
              locked && !isChecked && "opacity-60",
            )}
          >
            <input
              type="radio"
              name={name}
              value={optionValue}
              checked={isChecked}
              disabled={locked}
              onChange={() => onValueChange(optionValue)}
              className="mt-0.5 size-4 shrink-0 accent-primary"
            />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="flex items-center gap-1.5 text-[13px] font-medium text-foreground">
                <Icon className={cn("h-3.5 w-3.5", isChecked ? "text-primary" : "text-muted-foreground")} />
                {title}
              </span>
              <span className="text-xs text-muted-foreground">{description}</span>
            </span>
          </label>
        );
      })}
      {locked && (
        <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground sm:col-span-2">
          <Lock className="h-3 w-3" aria-hidden />
          {t("iam.templates.dialog.scopeLocked", "Set when the role was created")}
        </p>
      )}
    </div>
  );
}
